import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, MapPin, Calendar, Users, Sparkles, Plane, Camera, Heart, ChevronLeft, ChevronRight, Quote, Mountain, User, Leaf, Landmark } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Layout, PartnersSection } from '../components/Layout';
import BookingModal from '../components/BookingModal';
import SectionRenderer from '../components/SectionRenderer';
import { destinationsAPI, packagesAPI, cmsAPI } from '../../../services/api';
import { themes as defaultThemes } from '../../../data/mock';

// Icon mapping for themes
const iconMap = {
  Mountain: Mountain,
  Users: Users,
  Heart: Heart,
  Sparkles: Sparkles,
  User: User,
  Leaf: Leaf,
  Landmark: Landmark
};

// Fallback hero slides if CMS doesn't have any
const defaultHeroSlides = [
  {
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1800',
    title: 'Explore.',
    highlight: 'Experience.',
    subtitle: 'Remember.',
    description: 'Join our community of adventurers and discover incredible destinations with unforgettable experiences.',
    accent: 'from-rose-400 via-orange-400 to-amber-400'
  },
  {
    image: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1800',
    title: 'Dream.',
    highlight: 'Discover.',
    subtitle: 'Live.',
    description: 'From tropical beaches to snowy mountains, find your next adventure with us.',
    accent: 'from-cyan-400 via-teal-400 to-emerald-400'
  }
];

const Home = () => {
  const [pageData, setPageData] = useState(null);
  const [featuredDestinations, setFeaturedDestinations] = useState([]);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [trendingItems, setTrendingItems] = useState([]);
  const [spotlightBlogs, setSpotlightBlogs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showBooking, setShowBooking] = useState(false);

  // Fetch page content and data from API
  useEffect(() => {
    const fetchData = async () => {
      // allSettled — a single slow/failed endpoint must NOT collapse all sections.
      const results = await Promise.allSettled([
        destinationsAPI.getFeatured(8),
        packagesAPI.getFeatured(6),
        cmsAPI.getTrendingItems(8),
        cmsAPI.getSpotlightBlogs(3),
        cmsAPI.getTestimonials(8),
        cmsAPI.getPublicPage('home'),
      ]);
      const val = (i) => (results[i].status === 'fulfilled' ? results[i].value : null);

      setFeaturedDestinations(val(0) || []);
      setFeaturedPackages(val(1) || []);
      setTrendingItems(val(2) || []);
      setSpotlightBlogs(val(3) || []);
      setTestimonials(val(4) || []);
      setPageData(val(5));

      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          console.warn(`[Home] fetch[${i}] failed:`, r.reason?.message || r.reason);
        }
      });
      setLoading(false);
    };
    fetchData();
  }, []);

  // Get data from CMS or use defaults
  const heroSlides = pageData?.hero_slides?.length > 0 ? pageData.hero_slides : defaultHeroSlides;
  const heroCta = pageData?.hero_cta || 'Start Exploring';
  const heroCtaLink = pageData?.hero_cta_link || '/packages';
  const heroCta2 = pageData?.hero_cta_2 || 'Group Trips';
  const heroCtaLink2 = pageData?.hero_cta_link_2 || '/group-travel';
  const sections = pageData?.sections || [];

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  // Get section by type from CMS
  const getSection = (type) => sections.find(s => s.type === type);
  
  // Stats from CMS or defaults
  const statsSection = getSection('statsBar');
  const stats = statsSection?.data || [
    { value: '50,000+', label: 'Happy Travelers' },
    { value: '500+', label: 'Destinations' },
    { value: '10+', label: 'Years Experience' },
    { value: '4.9', label: 'Customer Rating' }
  ];

  // New sections from CMS
  const featuredDestSection = getSection('featuredDestinations');
  const trendingNowSection = getSection('trendingNow');
  const spotlightSection = getSection('spotlightBlogs');
  
  // Destinations section from CMS (legacy)
  const destSection = getSection('destinationsGrid');
  
  // Themes section from CMS
  const themesSection = getSection('themesGrid');
  const themes = themesSection?.cards?.length > 0 ? themesSection.cards : defaultThemes;

  // Packages section from CMS
  const pkgSection = getSection('packagesGrid');

  // Testimonials section from CMS
  const testSection = getSection('testimonials');

  // Partners section from CMS
  const partnersSection = getSection('partnersLogos');

  // CTA section from CMS
  const ctaSection = getSection('cta');

  return (
    <Layout>
      {/* Hero Section with Multiple Banners */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Animated Backgrounds */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center transform scale-105"
              style={{
                backgroundImage: slide.image ? `url(${slide.image})` : 'none',
                backgroundColor: '#334155',
                animation: index === currentSlide ? 'slowZoom 20s ease-out forwards' : 'none'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/50 to-transparent" />
          </div>
        ))}
        
        {/* Decorative shapes */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-rose-500/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-amber-400 mr-2" />
              <span className="text-white text-sm font-medium">{heroSlides[currentSlide]?.badge || 'Your adventure starts here'}</span>
            </div>
            
            {heroSlides.map((slide, index) => (
              <div
                key={index}
                className={`transition-all duration-700 ${
                  index === currentSlide ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 absolute'
                }`}
              >
                {index === currentSlide && (
                  <>
                    <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
                      {slide.title}
                      <span className={`block bg-gradient-to-r ${slide.accent || 'from-rose-400 via-orange-400 to-amber-400'} bg-clip-text text-transparent`}>
                        {slide.highlight}
                      </span>
                      <span className="block">{slide.subtitle}</span>
                    </h1>
                    <p className="text-xl text-slate-200 leading-relaxed mb-8">
                      {slide.description}
                    </p>
                  </>
                )}
              </div>
            ))}
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={heroCtaLink} target="_blank" rel="noopener noreferrer">
                <Button className="bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white px-8 py-6 text-base font-semibold rounded-full shadow-xl shadow-rose-500/30 transition-all duration-300 hover:shadow-2xl hover:scale-105">
                  {heroCta}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              <a href={heroCtaLink2} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-base font-semibold rounded-full transition-all duration-300">
                  <Users className="mr-2 w-5 h-5" />
                  {heroCta2}
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Slide Navigation */}
        {heroSlides.length > 1 && (
          <>
            <div className="absolute bottom-24 left-1/2 -translate-x-1/2 hidden lg:flex items-center gap-4">
              <button 
                onClick={prevSlide}
                className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
                    }`}
                  />
                ))}
              </div>
              <button 
                onClick={nextSlide}
                className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Mobile slide indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex lg:hidden gap-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'w-6 bg-white' : 'w-2 bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Render all sections from CMS in order */}
      {sections.map((section) => (
        <SectionRenderer 
          key={section.id} 
          section={section}
          data={{ destinations: featuredDestinations, packages: featuredPackages, testimonials }}
        />
      ))}

      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />
    </Layout>
  );
};

export default Home;
